// The Lobby's « Jouer » tab, its home: the game in progress, an invitation, then the ways to play as mode
// tiles — among friends (create a table, a code, the player's own tables), training against bots (their
// level, and the tutorial) — and below them online play (on the felt, with the open tables).
import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bot, Check, ChevronDown, DoorOpen, GraduationCap, Info, Link2, Plus, RefreshCw, Users, X, Zap,
} from "lucide-react";
import { PlayingCard } from "../PlayingCard";
import { JoinCodePanel } from "../JoinCodePanel";
import { InviteCard } from "../players/InviteCard";
import { ParamsPanel, isDefaultParams } from "./roomInfo";
import { matStyle } from "../../lib/tableThemes";
import { TUTORIAL } from "../../lib/features";
import { INVITE_LINKS, inviteUrl } from "../../lib/links";
import { toast } from "../../lib/notify";
import { t, trRoomName } from "../../lib/i18n";

const LEVELS = ["debutant", "medium", "legende"];
// The hero's fan of cards (decoration). The Joker, in the middle, sits in front of the two others (owner's request).
const FAN = [
  { id: "fan-a", rank: "A", suit: "spades" },
  { id: "fan-j", rank: "JOKER" },
  { id: "fan-k", rank: "K", suit: "hearts" },
];

// A mode tile: a gold corner, an icon, a title in capitals and one line under it; `action` at the end of
// that row.
function Tile({ icon: Icon, iconTone, title, sub, action, children, testId, className = "" }) {
  return (
    <section
      className={`relative overflow-hidden rounded-2xl border border-white/[0.07] bg-slate-900/80 px-3 py-2.5 shadow-[0_6px_24px_rgba(0,0,0,0.3)] ${className}`}
      data-testid={testId}
    >
      <span aria-hidden="true" className="absolute end-0 top-0 h-5 w-5 bg-gradient-to-bl from-amber-300/80 to-amber-600/40 [clip-path:polygon(0_0,100%_0,100%_100%)]" />
      <div className="flex items-center gap-2.5">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] ${iconTone}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[13px] uppercase leading-tight tracking-[0.12em] text-white">{title}</h3>
          <p className="text-[11px] leading-snug text-slate-400">{sub}</p>
        </div>
        {action}
      </div>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function OngoingGames({ games, quitting, onResume, onQuit }) {
  return games.map((g) => (
    <motion.div
      key={g.room_id}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-950/70 px-3 py-1.5"
      data-testid={`ongoing-${g.room_id}`}
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:hidden" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-300">{t("lobby.hub.ongoing")}</p>
        <p className="truncate text-sm text-white">{trRoomName(g.name)}</p>
      </div>
      <button
        type="button"
        onClick={() => onResume(g.room_id)}
        className="h-8 shrink-0 rounded-full bg-emerald-500 px-4 text-sm text-emerald-950 transition-colors hover:bg-emerald-400"
      >
        {t("lobby.resume")}
      </button>
      <button
        type="button"
        onClick={() => onQuit(g.room_id)}
        disabled={quitting === g.room_id}
        title={t("common.quit")}
        aria-label={t("common.quit")}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-rose-500/30 text-rose-300 transition-colors hover:bg-rose-500/15 disabled:opacity-40"
      >
        <DoorOpen className="h-4 w-4" />
      </button>
    </motion.div>
  ));
}

// « Jouer en ligne »: on the player's own felt with a fan of cards, its open tables below it, shown from the
// start (owner's request); the toggle folds them.
function OnlineHero({ themeId, rooms, busy, matchmaking, onPlay, onJoin, onRefresh, refreshing }) {
  const [listOpen, setListOpen] = useState(true);
  const [infoId, setInfoId] = useState(null);
  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-amber-400/30 shadow-[0_10px_32px_rgba(0,0,0,0.5)]"
      data-testid="hero-online"
    >
      <div className="rami-mat absolute inset-0" style={matStyle(themeId)} />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/65 to-slate-950/10 rtl:bg-gradient-to-l" />
      <div aria-hidden="true" className="pointer-events-none absolute end-1 top-0 h-[100px] w-[112px] scale-[0.68] sm:end-8 sm:top-1 sm:scale-[0.8]">
        {FAN.map((card, i) => (
          <div
            key={card.id}
            className="absolute bottom-0 left-1/2 origin-bottom drop-shadow-[0_10px_14px_rgba(0,0,0,0.6)]"
            style={{ transform: `translateX(-50%) rotate(${(i - 1) * 16}deg) translateY(${i === 1 ? -8 : 0}px)`, zIndex: i === 1 ? 1 : 0 }}
          >
            <PlayingCard card={card} size="md" />
          </div>
        ))}
      </div>
      <span aria-hidden="true" className="absolute end-0 top-0 h-7 w-7 bg-gradient-to-bl from-amber-200 to-amber-600 [clip-path:polygon(0_0,100%_0,100%_100%)]" />

      <div className="relative px-3.5 py-2.5 sm:px-5 sm:py-3.5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/20 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] text-sky-200 ring-1 ring-sky-400/30">
          <Users className="h-3 w-3" /> {t("lobby.hub.onlineTag")}
        </span>
        <h2 className="mt-1 text-xl uppercase leading-none tracking-[0.06em] text-white drop-shadow sm:text-2xl">
          {t("lobby.playOnline")}
        </h2>
        <p className="mt-1 max-w-[60%] text-[11px] leading-snug text-slate-300 sm:text-xs">{t("lobby.hub.onlineSub")}</p>
        <div className="mt-2.5 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setListOpen((o) => !o)}
            aria-expanded={listOpen}
            data-testid="open-tables-toggle"
            className="flex h-8 min-w-0 items-center gap-2 rounded-full border border-white/10 bg-slate-950/60 px-3 text-xs text-slate-200 transition-colors hover:border-sky-400/40"
          >
            <span className={`h-2 w-2 shrink-0 rounded-full ${rooms.length ? "bg-emerald-400" : "bg-slate-500"}`} />
            <span className="truncate">{t("lobby.hub.openTables", { n: rooms.length })}</span>
            <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${listOpen ? "rotate-180" : ""}`} />
          </button>
          <button
            type="button"
            onClick={onPlay}
            disabled={busy || matchmaking}
            title={busy ? t("lobby.quitFirst") : undefined}
            data-testid="play-online-btn"
            className="ms-auto flex h-9 shrink-0 items-center gap-2 rounded-full bg-gradient-to-b from-amber-300 to-amber-500 px-5 text-sm uppercase tracking-[0.14em] text-slate-950 shadow-[0_6px_20px_rgba(245,158,11,0.45)] transition-transform hover:brightness-110 active:scale-95 disabled:opacity-40 disabled:active:scale-100"
          >
            {matchmaking ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4 fill-current" />}
            {matchmaking ? t("lobby.searching") : t("lobby.play")}
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {listOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="relative overflow-hidden border-t border-white/10 bg-slate-950/85"
          >
            <div className="space-y-1 p-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{t("lobby.onlineTitle")}</span>
                <button
                  type="button"
                  onClick={onRefresh}
                  data-testid="refresh-rooms-btn"
                  aria-label={t("join.refresh")}
                  title={t("join.refresh")}
                  className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-slate-400 hover:text-white"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                </button>
              </div>
              {rooms.length === 0 ? (
                <p className="py-3 text-center text-xs text-slate-400">{t("lobby.noOnline")}</p>
              ) : (
                rooms.map((r) => {
                  const filled = r.members?.length ?? 0;
                  const full = filled >= r.num_players;
                  const showInfo = infoId === r.room_id;
                  return (
                    <div key={r.room_id} className={`rounded-xl border px-2.5 py-1.5 ${full ? "border-slate-800 opacity-50" : "border-slate-700/50 bg-slate-800/60"}`}>
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm text-white">{trRoomName(r.name)}</span>
                            <span className={`shrink-0 rounded-full border px-1.5 py-px text-[9px] uppercase ${isDefaultParams(r) ? "border-slate-600/50 text-slate-400" : "border-amber-500/20 text-amber-400"}`}>
                              {isDefaultParams(r) ? t("lobby.presetDefault") : t("lobby.presetCustom")}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {t("common.playersCount", { a: filled, b: r.num_players })}
                            {!full && <span className="ms-2 text-emerald-400">{t("lobby.spotsLeft", { n: r.num_players - filled })}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setInfoId(showInfo ? null : r.room_id)}
                          title={t("lobby.viewParams")}
                          className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-700/50 text-slate-400 hover:text-slate-200"
                        >
                          {showInfo ? <X className="h-3.5 w-3.5" /> : <Info className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          disabled={full || busy}
                          onClick={() => onJoin(r.room_id)}
                          className="h-7 rounded-lg border border-sky-500/25 bg-sky-500/15 px-3 text-xs text-sky-200 hover:bg-sky-500/25 disabled:opacity-40"
                        >
                          {full ? t("lobby.full") : t("common.join")}
                        </button>
                      </div>
                      {showInfo && <ParamsPanel r={r} />}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function FriendsTile({ busy, rooms, onCreate, onJoin }) {
  const [infoId, setInfoId] = useState(null);
  return (
    <Tile icon={Users} iconTone="text-amber-300" title={t("lobby.hub.friendsTitle")} sub={t("lobby.hub.friendsSub")} testId="tile-friends">
      <button
        type="button"
        onClick={onCreate}
        disabled={busy}
        title={busy ? t("lobby.quitFirst") : undefined}
        data-testid="create-room-btn"
        className="flex h-8 w-full items-center gap-2 rounded-full bg-amber-500 px-4 text-sm text-slate-950 shadow-lg shadow-amber-500/20 transition-colors hover:bg-amber-400 disabled:opacity-35"
      >
        <Plus className="h-4 w-4" /> {t("lobby.hub.create")}
      </button>
      <JoinCodePanel className="mt-2" disabled={busy} disabledTitle={t("lobby.quitFirst")} />
      {rooms.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{t("lobby.hub.yourTables")}</p>
          {rooms.map((r) => {
            const showInfo = infoId === r.room_id;
            return (
              <div key={r.room_id} className="rounded-xl border border-slate-700/40 bg-slate-800/50 px-3 py-1.5" data-testid={`room-card-${r.room_id}`}>
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm text-white">{trRoomName(r.name)}</span>
                      {r.rematch_of && (
                        <span className="shrink-0 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-1.5 py-px text-[9px] uppercase text-emerald-300">
                          {t("common.rematchBadge")}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">{t("common.playersCount", { a: r.members.length, b: r.num_players })}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setInfoId(showInfo ? null : r.room_id)}
                    title={t("lobby.viewParams")}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 hover:text-slate-300"
                  >
                    {showInfo ? <X className="h-3.5 w-3.5" /> : <Info className="h-3.5 w-3.5" />}
                  </button>
                  {/* The website keeps the invite link; the apps share the code (INVITE_LINKS in lib/links.js). */}
                  {!r.rematch_of && INVITE_LINKS && (
                    <button
                      type="button"
                      title={t("lobby.copyLink")}
                      onClick={() => { navigator.clipboard.writeText(inviteUrl(r.room_id)); toast.success(t("lobby.linkCopied")); }}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 hover:text-amber-300"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onJoin(r.room_id)}
                    // A full rematch room still lets its players in: a bot gives the seat back.
                    disabled={(r.members.length >= r.num_players && !r.rematch_of) || busy}
                    data-testid={`join-room-btn-${r.room_id}`}
                    className="h-7 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 text-xs text-amber-200 hover:bg-amber-500/20 disabled:opacity-40"
                  >
                    {t("common.join")}
                  </button>
                </div>
                {showInfo && <ParamsPanel r={r} />}
              </div>
            );
          })}
        </div>
      )}
    </Tile>
  );
}

// « Tutoriel », in the training tile's title row (owner's request): where a player goes to learn, and the
// tutorial's last step itself offers a game against a beginner bot. Until it has been seen once, a solid gold
// button with dark text and a soft pulsing ring (owner: the first, pale one could not be read); after, a dark
// one with white text and a check. It keeps clear of the tile's gold corner.
function TutorialPill({ seen, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="tutorial-btn"
      title={t("tuto.button")}
      className={`relative me-6 flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-xs transition-colors ${
        seen
          ? "border border-white/15 bg-slate-800 text-white hover:border-amber-400/50"
          : "bg-amber-400 text-slate-950 shadow-[0_4px_14px_rgba(251,191,36,0.35)] hover:bg-amber-300"
      }`}
    >
      {!seen && (
        // A ring that pulses in place (a growing one, `animate-ping`, drew a large circle over the tile).
        <span aria-hidden="true" className="absolute -inset-1 animate-pulse rounded-full ring-2 ring-amber-300/60 motion-reduce:hidden" />
      )}
      <GraduationCap className={`relative h-4 w-4 shrink-0 ${seen ? "text-amber-300" : ""}`} />
      <span className="relative whitespace-nowrap">{t("tuto.button")}</span>
      {seen && <Check className="relative h-3.5 w-3.5 shrink-0 text-emerald-300" />}
    </button>
  );
}

function TrainingTile({ busy, level, onLevel, onSolo, tutorialSeen, onTutorial }) {
  const solo = (n, testId, label) => (
    <button
      type="button"
      onClick={() => onSolo(n)}
      disabled={busy}
      title={busy ? t("lobby.quitFirst") : undefined}
      data-testid={testId}
      className="flex h-8 items-center justify-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 text-sm text-emerald-100 transition-colors hover:bg-emerald-500/20 disabled:opacity-35"
    >
      <Bot className="h-4 w-4 shrink-0 text-emerald-300" />
      <span className="truncate">{label}</span>
    </button>
  );
  return (
    <Tile
      icon={Bot}
      iconTone="text-emerald-300"
      title={t("lobby.hub.trainingTitle")}
      sub={t("lobby.hub.trainingSub")}
      action={TUTORIAL && <TutorialPill seen={tutorialSeen} onOpen={onTutorial} />}
      testId="tile-training"
    >
      <div className="grid grid-cols-2 gap-2">
        {solo(2, "solo-2p-btn", t("lobby.hub.solo2"))}
        {solo(4, "solo-4p-btn", t("lobby.hub.solo4"))}
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-[0.16em] text-slate-500">{t("lobby.hub.level")}</p>
      <div role="radiogroup" aria-label={t("lobby.hub.level")} className="mt-1.5 grid grid-cols-3 gap-1 rounded-full bg-slate-950/70 p-1">
        {LEVELS.map((id) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={level === id}
            onClick={() => onLevel(id)}
            data-testid={`solo-level-${id}`}
            className={`truncate rounded-full px-1 py-1 text-[11px] transition-colors ${level === id ? "bg-emerald-400 text-emerald-950" : "text-slate-400 hover:text-white"}`}
          >
            {t(`lobby.level.${id}`)}
          </button>
        ))}
      </div>
    </Tile>
  );
}

export function PlayTab({
  themeId, activeGames, quitting, onResume, onQuit,
  invite, inviteCount, onAnswerInvite, inviteActing, inviteError,
  onlineRooms, matchmaking, onPlayOnline, onJoinOnline, onRefresh, refreshing,
  rooms, onCreate, onJoinRoom,
  level, onLevel, onSolo, tutorialSeen, onTutorial, children,
}) {
  const busy = activeGames.length > 0;
  return (
    <div className="space-y-2">
      <OngoingGames games={activeGames} quitting={quitting} onResume={onResume} onQuit={onQuit} />
      {invite && (
        <InviteCard invite={invite} onAnswer={onAnswerInvite} acting={inviteActing} error={inviteError} busy={busy} more={inviteCount - 1} />
      )}
      {/* The tiles first, then « Jouer en ligne » with its open tables (owner's order). */}
      <div className="grid gap-2 sm:grid-cols-2">
        <FriendsTile busy={busy} rooms={rooms} onCreate={onCreate} onJoin={onJoinRoom} />
        <TrainingTile busy={busy} level={level} onLevel={onLevel} onSolo={onSolo} tutorialSeen={tutorialSeen} onTutorial={onTutorial} />
      </div>
      <OnlineHero
        themeId={themeId}
        rooms={onlineRooms}
        busy={busy}
        matchmaking={matchmaking}
        onPlay={onPlayOnline}
        onJoin={onJoinOnline}
        onRefresh={onRefresh}
        refreshing={refreshing}
      />
      {children}
    </div>
  );
}

export default PlayTab;
